import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import { GripVertical } from "lucide-react";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";

import Message from "@/components/Playground/Messages/Message";
import { IMessageStore } from "@/components/Playground/Messages/Message/message-store";
import MessageActions from "@/components/Playground/Messages/Message/MessageActions";
import RoleSelector from "@/components/Playground/Messages/Message/RoleSelector";
import { usePlaceholderContainer } from "@/components/Playground/Messages/PlaceholderContainer/context/placeholder-container-context";
import { PlaceholderErrorBoundary } from "@/components/Playground/Messages/PlaceholderContainer/errors/ErrorBoundary";
import { MessageFallback } from "@/components/Playground/Messages/PlaceholderContainer/errors/ErrorBoundary/Fallbacks";
import { MessageLevelError } from "@/components/Playground/Messages/PlaceholderContainer/errors/MessageLevelError";
import { MessageType } from "@/components/Playground/types";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export const PlaceholderChildMessage = observer(
  ({
    message,
    index,
    dragHandleProps,
  }: {
    message: IMessageStore;
    index: number;
    dragHandleProps?: DraggableProvidedDragHandleProps;
  }) => {
    const { readonly, childStore } = usePlaceholderContainer();

    const handleDeleteChildMessage = () => {
      runInAction(() => {
        childStore.deleteMessage(message.id);
      });
    };

    const handleCopy = () => {
      navigator.clipboard.writeText(message.fullTextContent);
    };

    const handleChildRoleSelect = (childMessage: IMessageStore) => (role: MessageType) => {
      if (role !== "placeholder") {
        childMessage.setRole(role);
      }
    };

    // Check for validation errors on this message
    const validationErrors = childStore.getErrorsForMessage(message.id);
    const hasError = validationErrors.length > 0;

    return (
      <PlaceholderErrorBoundary fallback={<MessageFallback />}>
        <div className={cn("relative rounded-lg", hasError && "ring-2 ring-red-400")}>
          {/* Child message header */}
          {hasError && <MessageLevelError messageId={message.id} childStore={childStore} />}
          <div className="flex items-center gap-x-2 border-b border-gray-100 px-3 py-2">
            {/* Drag handle */}
            {!readonly && (
              <div
                {...dragHandleProps}
                className="flex h-7 w-5 cursor-grab items-center justify-center rounded-md transition-colors hover:bg-gray-100 active:cursor-grabbing"
              >
                <GripVertical height={14} width={14} className="text-gray-400" />
              </div>
            )}

            <RoleSelector
              selected={message.role}
              readonly={readonly}
              onSelect={handleChildRoleSelect(message)}
              mode="chat"
            />
            {message.hasName && (!readonly || (readonly && message.name)) && (
              <Input
                value={message.name || ""}
                disabled={readonly}
                onChange={e => message.setName(e.target.value)}
                className="h-8 font-mono text-sm"
                placeholder={message.role === "tool" ? "Tool ID" : "Function Name"}
              />
            )}
            <div className="relative z-20 ml-auto flex gap-x-2">
              <MessageActions
                readonly={readonly}
                onCopy={handleCopy}
                onDelete={handleDeleteChildMessage}
              />
            </div>
          </div>

          {/* Child message content */}
          <Message
            message={message}
            index={index}
            isCompletion={false}
            isFirst={false}
            readonly={readonly}
            className="!px-2 !py-2"
            mode="chat"
          />
        </div>
      </PlaceholderErrorBoundary>
    );
  },
);
